import { Portal } from '@radix-ui/react-portal';
import React, { Fragment, UIEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { Close24 } from 'ui/icons/action/Close24';
import { colors } from 'ui/theme/colors';
import {
  OverlayBodyOverrides,
  SLIDEOVER_EXIT_ANIMATION_DURATION_IN_MS,
  SlideOverCloseBtn,
  SlideOverContent,
  SlideOverContentWrapper,
  SlideOverFooter,
  SlideOverHeader,
  SlideOverOverlay,
  SlideOverWrapper,
} from './SlideOver.styled';

export const SLIDE_OVER_OVERLAY_ID = 'slideover-overlay';

export interface SlideOverProps {
  /** whether grayed out background overlay is shown */
  hasOverlay?: boolean;
  /** id will be appended to data-test attributes */
  id: string;
  /** When true, the slide over is shown. */
  isOpen: boolean;
  /** Whether to render the slideover in a portal */
  withPortal?: boolean;
  /** Content of the header */
  headerContent?: JSX.Element;
  /** Content of the body */
  bodyContent?: JSX.Element;
  /** Content of the header */
  footerContent?: React.ReactNode;
  /** Callback fired when the slide over is requested to be closed. */
  onClose?: () => void;
  /** Callback fired when click outside of slide over content */
  onClickOutside?: () => void;
  /**
   * Callback fired when the slide over is requested to be closed.
   * This will stop the slideover from closing until you call the `close` function.
   *
   * @param close - function to close the slide over if you want to proceed with the request to close.
   */
  onRequestClose?: (close: () => void) => void;
  /** Callback fired when the slide over is scrolled */
  onScroll?: (e: UIEvent<HTMLDivElement>) => void;
  /** Content background color, default is colors.grey1 */
  bodyBgColor?: string;
}

export const SlideOver = ({
  hasOverlay = true,
  id,
  isOpen,
  withPortal = true,
  onClose,
  headerContent,
  bodyContent,
  footerContent,
  onClickOutside,
  onRequestClose,
  onScroll,
  bodyBgColor = colors.grey1,
  ...props
}: SlideOverProps) => {
  const wrapperRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isShown, setIsShown] = useState(isOpen);
  const [isExitingSlideoverAnimation, setIsExitingSlideoverAnimation] = useState(false);

  const handleClose = useCallback(() => {
    const close = () => {
      setIsExitingSlideoverAnimation(true);
      setTimeout(() => {
        setIsShown(false);
        onClose?.();
        setIsExitingSlideoverAnimation(false);
      }, SLIDEOVER_EXIT_ANIMATION_DURATION_IN_MS);
    };

    if (onRequestClose) {
      onRequestClose?.(close);
    } else {
      close();
    }
  }, [setIsShown, onClose, onRequestClose]);

  useEffect(() => {
    setIsShown(isOpen);

    const close = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [isOpen, handleClose]);

  useClickAway(
    wrapperRef,
    (e) => {
      // other popups may exist, like for instance, a toast or modal. without this check,
      // clicking on a modal would close the slideover, which may not be an intended interaction.
      // so we only close the slideover, if a user clicks on the overlay.
      if (
        e.target instanceof HTMLElement &&
        e.target?.dataset?.overlayId === SLIDE_OVER_OVERLAY_ID
      ) {
        const close = () => {
          setIsExitingSlideoverAnimation(true);
          setTimeout(() => {
            onClickOutside?.();
            setIsExitingSlideoverAnimation(false);
          }, SLIDEOVER_EXIT_ANIMATION_DURATION_IN_MS);
        };

        if (onRequestClose) {
          onRequestClose?.(close);
        } else {
          close();
        }
      }
    },
    ['mousedown'],
  );

  const onSlideOverScroll = (e: UIEvent<HTMLDivElement>) => {
    const currentScrollY = (e.target as HTMLElement).scrollTop;
    onScroll?.(e);
    if (currentScrollY === 0) {
      setIsScrolled(false);
    } else {
      setIsScrolled(true);
    }
  };

  const PortalWrapper = withPortal ? Portal : Fragment;

  return (
    isShown && (
      <PortalWrapper asChild>
        <div {...props}>
          {hasOverlay && (
            <>
              <SlideOverOverlay
                data-animation-exiting={isExitingSlideoverAnimation}
                data-is-shown="true"
                open
                data-test={`${id}-${SLIDE_OVER_OVERLAY_ID}`}
                data-overlay-id={SLIDE_OVER_OVERLAY_ID}
              />
              <OverlayBodyOverrides />
            </>
          )}
          <SlideOverWrapper
            data-animation-exiting={isExitingSlideoverAnimation}
            data-is-shown={isShown}
            $isExpanded={isShown}
            data-test={`${id}-slideover`}
          >
            <SlideOverContentWrapper
              ref={wrapperRef}
              $isExpanded={isShown}
              $bodyBgColor={bodyBgColor}
              data-test={`${id}-slideover-content-wrapper`}
            >
              <SlideOverHeader $isScrolled={isScrolled} data-test={`${id}-slideover-header`}>
                <div>{headerContent}</div>
                <SlideOverCloseBtn>
                  <IconButton
                    data-test={`${id}-close-slideover-button`}
                    variant={BUTTON_VARIANTS.GHOST}
                    size={BUTTON_SIZES.MEDIUM}
                    onClick={handleClose}
                  >
                    <Close24 />
                  </IconButton>
                </SlideOverCloseBtn>
              </SlideOverHeader>

              <SlideOverContent onScroll={onSlideOverScroll} data-test={`${id}-slideover-content`}>
                {bodyContent}
              </SlideOverContent>

              {footerContent && (
                <SlideOverFooter data-test={`${id}-slideover-footer`}>
                  {footerContent}
                </SlideOverFooter>
              )}
            </SlideOverContentWrapper>
          </SlideOverWrapper>
        </div>
      </PortalWrapper>
    )
  );
};
